import React from 'react';
import { Box, Button, ButtonBase, Tooltip, Typography } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Employee } from '../../../store/slices/employeeSlice';
import { CountBadge, PersonAvatar, StatusPill, Tag } from '../../../components/ui';
import { focusRing, sbg, transition } from '../../../theme/sbgTokens';
import { formatCount, formatNumber } from '../../../utils/format';
import { getEmployeeTeamLabel, jobTitleOf, LANE_PREVIEW, SalesManagerLane, SelectVia } from './orgChartModel';
import { HighlightText, revealRowActionsSx, RowActions, viaOf } from './OrgChartBits';

export interface TeamLaneProps {
  lane: SalesManagerLane;
  searchTerm: string;
  filterActive: boolean;
  reportsByManager: Map<string, Employee[]>;
  dragOverLane: string | null;
  draggedId: string | null;
  savingId: string | null;
  focusedId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragEnter: (laneId: string) => void;
  onDragLeave: () => void;
  onDrop: (lane: SalesManagerLane) => void;
  onEdit: (employee: Employee) => void;
  onViewProfile: (id: string) => void;
  onSelect: (id: string, via?: SelectVia) => void;
  canEdit: boolean;
  canViewProfile: boolean;
}

/**
 * One Sales manager and their direct reports; a drop target for moving people
 * between teams. Lanes size to their rows (the page is the only scroll area)
 * and show the first eight people until "Show all" is pressed.
 */
const TeamLane = React.memo<TeamLaneProps>(({
  lane,
  searchTerm,
  filterActive,
  reportsByManager,
  dragOverLane,
  draggedId,
  savingId,
  focusedId,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDrop,
  onEdit,
  onViewProfile,
  onSelect,
  canEdit,
  canViewProfile,
}) => {
  const isDropTarget = dragOverLane === lane.id;
  const managerSelected = focusedId === lane.manager._id;
  const subtitle = lane.teamName === lane.departmentName ? lane.teamName : `${lane.teamName} · ${lane.departmentName}`;
  const emptyMessage = lane.employees.length === 0
    ? 'No direct reports yet.'
    : filterActive && lane.visibleEmployees.length === 0
      ? 'No matches in this team.'
      : null;
  const [showAll, setShowAll] = React.useState(false);
  const listId = React.useId();
  const selectedIndex = focusedId ? lane.visibleEmployees.findIndex((employee) => employee._id === focusedId) : -1;
  // Someone picked from search or the detail panel is never hidden behind "Show all".
  React.useEffect(() => {
    if (selectedIndex >= LANE_PREVIEW) setShowAll(true);
  }, [selectedIndex]);
  const overflow = lane.visibleEmployees.length - LANE_PREVIEW;
  const rows = showAll || overflow <= 0 ? lane.visibleEmployees : lane.visibleEmployees.slice(0, LANE_PREVIEW);

  return (
    <Box
      component="section"
      aria-label={`${lane.manager.name}'s team`}
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={() => {
        if (canEdit) onDragEnter(lane.id);
      }}
      onDragLeave={canEdit ? onDragLeave : undefined}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        onDrop(lane);
      }}
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: sbg.radius.lg,
        border: `1px solid ${isDropTarget ? sbg.border.selected : sbg.border.default}`,
        boxShadow: isDropTarget ? `0 0 0 1px ${sbg.border.selected}` : 'none',
        bgcolor: isDropTarget ? sbg.bg.selected : sbg.bg.surface,
        transition: transition('border-color', 'background-color', 'box-shadow'),
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${sbg.border.subtle}`, display: 'grid', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <ButtonBase
            onClick={(event) => onSelect(lane.manager._id, viaOf(event))}
            data-select-person={lane.manager._id}
            aria-pressed={managerSelected}
            aria-label={`Select ${lane.manager.name}`}
            sx={{
              flex: 1,
              minWidth: 0,
              justifyContent: 'flex-start',
              gap: 1.25,
              p: 0.5,
              m: -0.5,
              borderRadius: sbg.radius.md,
              textAlign: 'left',
              '&:hover': { bgcolor: sbg.bg.hover },
              '&.Mui-focusVisible': focusRing,
            }}
          >
            <PersonAvatar name={lane.manager.name} size={32} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="labelMd" component="div" noWrap sx={{ color: managerSelected ? sbg.text.accent : sbg.text.primary }}>
                {lane.manager.name}
              </Typography>
              <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                {subtitle}
              </Typography>
            </Box>
          </ButtonBase>
          <Tooltip title={formatCount(lane.directCount, 'direct report')}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <CountBadge count={lane.directCount} aria-label={formatCount(lane.directCount, 'direct report')} />
            </Box>
          </Tooltip>
        </Box>
        {(!lane.linked || lane.source !== 'reports') && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {!lane.linked && (
              <StatusPill tone="warning" title="No Attendance login is linked to this manager yet.">
                Login not linked
              </StatusPill>
            )}
            {lane.source !== 'reports' && <Tag>Dept manager</Tag>}
          </Box>
        )}
      </Box>

      <Box
        component="ul"
        id={listId}
        sx={{
          listStyle: 'none',
          m: 0,
          p: 1,
          flex: 1,
          display: 'grid',
          alignContent: 'start',
          gap: 0.5,
        }}
      >
        {rows.map((employee) => {
          const reportsCount = (reportsByManager.get(employee._id) || []).length;
          const isSelected = focusedId === employee._id;
          const isDragging = draggedId === employee._id;
          const isSaving = savingId === employee._id;
          // The lane already names the team; repeat it only for someone filed under another one.
          const teamLabel = getEmployeeTeamLabel(employee);
          const secondary = jobTitleOf(employee) ?? (teamLabel !== lane.teamName ? teamLabel : null);

          return (
            <Box
              component="li"
              key={employee._id}
              draggable={canEdit && !isSaving}
              aria-busy={isSaving || undefined}
              data-selected={isSelected || undefined}
              onDragStart={(event) => {
                if (!canEdit) return;
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', employee._id);
                onDragStart(employee._id);
              }}
              onDragEnd={canEdit ? onDragEnd : undefined}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minHeight: 48,
                pl: 1,
                pr: 0.5,
                borderRadius: sbg.radius.md,
                border: `1px solid ${isSelected ? sbg.border.selected : 'transparent'}`,
                bgcolor: isSelected ? sbg.bg.selected : 'transparent',
                opacity: isDragging || isSaving ? 0.5 : 1,
                cursor: isSaving ? 'wait' : canEdit ? 'grab' : 'pointer',
                transition: transition('background-color', 'border-color', 'opacity'),
                '&:hover': { bgcolor: isSelected ? sbg.bg.selected : sbg.bg.hover },
                ...revealRowActionsSx,
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                data-select-person={employee._id}
                aria-pressed={isSelected}
                onClick={() => onSelect(employee._id, 'pointer')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(employee._id, 'keyboard');
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:focus-visible': { outline: 'none' },
                  '&::after': { content: '""', position: 'absolute', inset: -1, borderRadius: sbg.radius.md },
                  '&:focus-visible::after': focusRing,
                }}
              >
                <PersonAvatar name={employee.name} size={24} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
                    <HighlightText text={employee.name} highlight={searchTerm} />
                  </Typography>
                  {secondary && (
                    <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                      {secondary}
                    </Typography>
                  )}
                </Box>
              </Box>
              {reportsCount > 0 && (
                <Tooltip title={formatCount(reportsCount, 'direct report')}>
                  <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', mr: 0.5 }}>
                    <CountBadge count={reportsCount} aria-label={formatCount(reportsCount, 'direct report')} />
                  </Box>
                </Tooltip>
              )}
              <RowActions
                employee={employee}
                canEdit={canEdit}
                canViewProfile={canViewProfile}
                onEdit={onEdit}
                onViewProfile={onViewProfile}
              />
            </Box>
          );
        })}

        {emptyMessage && (
          <Box
            component="li"
            sx={{
              minHeight: 56,
              display: 'grid',
              placeItems: 'center',
              borderRadius: sbg.radius.md,
              border: `1px dashed ${sbg.border.default}`,
              px: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
              {emptyMessage}
            </Typography>
          </Box>
        )}
      </Box>

      {overflow > 0 && (
        <Box sx={{ px: 1, pb: 1 }}>
          <Button
            variant="text"
            size="small"
            fullWidth
            aria-expanded={showAll}
            aria-controls={listId}
            startIcon={showAll ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            onClick={() => setShowAll((current) => !current)}
          >
            {showAll ? 'Show fewer' : `Show all ${formatNumber(lane.visibleEmployees.length)}`}
          </Button>
        </Box>
      )}
    </Box>
  );
});

TeamLane.displayName = 'TeamLane';

export default TeamLane;
